<template>
	<anchor-element tag="span" :chain="chain">
		<div ref="mentionFieldEl" :contenteditable="!ro" v-bind="tabindexBinding()"
			 @keyup="onKeyUp" @keydown="onKeyDown" @click="onClick" @focusout="onFocusOut" 
			 :class="[{ 'mentionFieldDis': ro }, clazz, errorClass, 'likeInput', 'mentionField']" :style="style" v-html="value">
		</div>
	</anchor-element>
	<boat-element v-if="showSuggests && chain.visible" :chain="chain" >
		<ul class="autocompleteAbsoluteList" style="width: 250px;" >
			<li v-for="(item,index) in results" class="autocompleteItem" :class="{ autocompleteItemSel: selected===index }" :key="item.key"
				 @click="selectResult(item)" @mouseenter="setSelected(index)" >{{item.value}}</li>
		</ul>
	</boat-element>
	<div class="errors" v-if="isError"><span class="perr">{{errorMessage}}</span></div>
</template>



<script setup lang="ts">
	import { ref, computed, onMounted,nextTick } from 'vue';
	import AnchorElement from "@/ui/paneltree/AnchorElement.vue";
	import BoatElement from "@/ui/paneltree/BoatElement.vue";
	import {createChain, DROPDOWN_PLACEMENT} from "@/ui/paneltree/PanelTree";
	import { useAutocomplete, SuggestResult } from './Autocomplete';
	import { MentionObject, MENTION_CHAR } from './MentionUtils';
	import {previewKey} from "@fd/helpers/Preview";
	import useEditor, {type EditorProps, editorProps} from "@fd/editors/Editor";
	import type {Mentions} from "@t/fd/editor/Mentions";
	
	
	/*const props = defineProps({
		endpoint: { type: String, default: "api/parties/suggest" },
		endpointParams: { type: String, default: "mode=login" },
		endpointMode: { type: String, default: "login" },
		tabindex: { type: Number, default: 0 },
		modelValue: { type: String, default: "" },
		value: { type: String, default: "" },
		readonly: Boolean,
		focus: Boolean
	});*/
	
	/*const props = withDefaults(defineProps<{
		prdel:boolean,
		frdel?:string
	}>(), { frdel: "ahoj" });*/
	
	const uprops = defineProps({...editorProps});
	const props = uprops as EditorProps;
	const mentionsProps = computed<Mentions>(() => props.part as Mentions);
	const {visible, value, clazz, style, ro, errorClass, tabindexBinding} = useEditor(props);
	
	//set defaults
	if (!mentionsProps.endpoint) {
		mentionsProps.endpoint = "api/parties/suggest";
	}
	if (!mentionsProps.endpointParams) {
		mentionsProps.endpointParams = "mode=login";
	}
	if (!mentionsProps.endpointMode) {
		mentionsProps.endpointMode = "login";
	}
	
	const emit = defineEmits(['update:modelValue','updateValue']);
	
	const { results, selected, errorMessage, isError, progress, inputValue, 
		sendData, selectResult, selectSelectedResult, resetResults, 
		setSelected, goUp, goDown } = useAutocomplete(mentionsProps,emit,afterSelectResult);
	
	
	const mentionFieldEl = ref(null);
	const mention = ref<MentionObject>(null)
	const autocompletePosition = ref<ChainPlacement>({ah: 0, av: 100, bh: 0, bv: 0, x: 0, y: 0});
	
	const showSuggests = computed<boolean>(() => results.value.length>0);
	const editingMention = computed<boolean>(() => Boolean(mention.value));
	
	const chain = createChain({rule: autocompletePosition.value}, "first");

	function onKeyUp(): void {
		if (showSuggests.value &&  (event.key === "Enter" || event.key === "ArrowUp" || event.key === "ArrowDown")) {
			event.preventDefault();
			return;
		}
		if (editingMention.value) {
			var node: Node = window.getSelection().anchorNode;
			if (node.nodeType===3) {
				//console.log("node text:"+node.textContent);
				var text: string = node.textContent;
				if (text.startsWith(MENTION_CHAR)) {
					checkAutocomplete(node.textContent.substring(1));
				}
			}
		}
		if (event.key === "Escape" || event.key === "Tab" || event.key === "Enter" 
			|| event.key === ' ' || event.key === '.' || event.key === ',') {
			if (editingMention.value) {
				destroyMention(true);
			}
		}
	}
	
	function onKeyDown(): void {
		if (event.key === MENTION_CHAR) {
			createMention();
			event.preventDefault();
		}
		onKeyDownAutocomplete(event);
	}
	
	function onKeyDownAutocomplete(event: Event): void {
		if (showSuggests.value) {
			if (event.key === "Enter") {
				selectSelectedResult();
				event.preventDefault();
				return;
			}
			else if (event.key === "ArrowUp") {
				goUp();
				event.preventDefault();
				return;
			}
			else if (event.key === "ArrowDown") {
				goDown();
				event.preventDefault();
				return;
			}
		}
	}
	
	function onClick(): void {
		if (editingMention.value) {
			destroyMention(false);
		}
	}
	
	function checkAutocomplete(text: string): void {
		if (text.length>=3) {
			inputValue.value=text;
			sendData(true);
			chain.visible.value = true;
		} else {
			inputValue.value=null;
			results.value=[];
		}
	}
	
	function afterSelectResult(result: SuggestResult): void {
		if (editingMention.value) {
			//console.log("selected result: "+JSON.stringify(result));
			mention.value.updateWithResult(result);
			close();
			updateValue();
			nextTick(() => {
				moveCursorToPosition(mention.value.position);
				//insertTextOnCursor(" ");
				mention.value = null;
			});
		}
	}
	
	function close(): void {
		inputValue.value=null;
		results.value=[];
		chain.visible.value = false;
	}
	
	
	function createMention(): void {
		const range = window.getSelection().getRangeAt(0);

		const clonedRange = range.cloneRange();
		clonedRange.selectNodeContents(mentionFieldEl.value);
		clonedRange.setEnd(range.endContainer, range.endOffset);
		const position = clonedRange.toString().length;
		//console.log("r:"+cursorPosition);
		mention.value = new MentionObject(position);
		range.insertNode(mention.value.element);
		moveCursorToEnd(mention.value.element);
		setAutocompletePosition();
	}
	
	function destroyMention(setCursor: boolean): void {
		console.log("destroy mention");
		close();
		const text = mention.value.element.innerText;
		mention.value.element.after(text);
		mention.value.element.remove();
		if (setCursor) {
			moveCursorToPosition(mention.value.position+text.length);
		}
		mention.value = null;
	}
	
	function insertTextOnCursor(text: string): void {
		var textNode = document.createTextNode(text);
		const range = window.getSelection().getRangeAt(0);
		range.insertNode(textNode);
		range.setStart(textNode, textNode.length);
		range.collapse(true);
	}
	
	function moveCursorToEnd(el: Element): void {
		el.focus();
		const range = document.createRange();
		const selection = window.getSelection();
		range.setStart(el, el.childNodes.length);
		range.collapse(true);
		selection.removeAllRanges();
		selection.addRange(range);
	}

	function moveCursorToPosition(targetPosition: number): void {
		const range = getRangeOfMentionPosition(targetPosition);
		const selection = window.getSelection();
		selection.removeAllRanges();
		selection.addRange(range);
	};

	function getRangeOfMentionPosition(targetPosition: number): Range {
		let range: Range = document.createRange();
		range.selectNode(mentionFieldEl.value);

		let pos = 0;
		const stack = [mentionFieldEl.value];
		while (stack.length > 0) {
			//console.log("pos:"+pos+" vs"+targetPosition);
			const current = stack.pop();

			if (current.nodeType === Node.TEXT_NODE) {
				//console.log("currrent text node:"+current.textContent);
				const len = current.textContent.length;
				if (pos + len > targetPosition) {
					range.setEnd(current, targetPosition - pos);
					//console.log("range in this to:"+(targetPosition - pos));
					range.collapse();
					return range;
				}
				pos += len;
			} else if (current.childNodes && current.childNodes.length > 0) {
				for (let i = current.childNodes.length - 1; i >= 0; i--) {
					stack.push(current.childNodes[i]);
				}
			}
		}

		//console.log("go to end");
		range.setEnd(mentionFieldEl.value, mentionFieldEl.value.childNodes.length);
		range.collapse();
		return range;
	}
	
	function updateValue(): void {
		value.value=mentionFieldEl.value.innerHTML;
		emit('update:modelValue', value.value);
		emit('updateValue', value.value);
	}
	
	function onFocusOut(): void {
		if (!editingMention.value) {
			updateValue();
		}
		else {
			setTimeout(()=>{
				if (editingMention.value) {
					console.log("focusout");
					destroyMention(false);
					updateValue();
				}
			}, 300);
		}
	}
	
	function setAutocompletePosition(): void {
		const rectParent = mentionFieldEl.value.getBoundingClientRect();
		const rectChild = mention.value.element.getBoundingClientRect();
	
		const posX = rectChild.left-rectParent.left+10; 
		const posY = rectChild.top-rectParent.top+20; 
		const width = mentionFieldEl.value.offsetWidth;
		const height = mentionFieldEl.value.offsetHeight;
		
		var x = (posX*100)/width;
		var y = (posY*100)/height; 
		if (x>100) x=100;
		if (y>100) y=100;
		
		console.log("x:"+x+",y:"+y);
		autocompletePosition.value.ah = x;
		autocompletePosition.value.av = y;
	}
		
	onMounted(() => {
		//initPreview(mentionFieldEl.value);
	});
</script>
<style>
.mentionField {
	overflow: hidden !important;
	height: 66px !important;
}

.mentionFieldDis {
	background-color: var(--input-disabled-bg) !important;
    color: var(--input-disabled-fg) !important;
}

.mentionText {
	color: var(--hover-on-light);
	font-weight: bold;
}

.mention {
	background-color: var(--input-bg);
	font-weight: bold;
	margin: 0px;
	padding: 0px;
	color: var(--hover-on-light);
	border: 0;
	min-width: 15px;
	display: inline-flex;
}

.mentionFound {
	color: var(--hover-on-light);
}

.mentionNotFound {
	color: var(--fact-fg);
}
</style>
